import { useEffect, useState } from 'react';
import {
  FieldType,
  OPTION_FIELD_TYPES,
  type FieldOption,
  type FieldsSchemaDto,
} from '@ciimar/shared';

/** A schema section carries its fields (unlike the bare FieldSectionDto). */
type SchemaSection = FieldsSchemaDto['sections'][number];
import { api } from '../api/client';
import { BilingualLabelInput } from '../components/BilingualLabelInput';
import { useT } from '../lib/i18n';

/** Field-type labels, resolved via the translator at render time. */
const TYPE_LABEL_KEYS: Record<FieldType, string> = {
  [FieldType.Text]: 'settings.fields.type.text',
  [FieldType.LongText]: 'settings.fields.type.longText',
  [FieldType.Number]: 'settings.fields.type.number',
  [FieldType.Integer]: 'settings.fields.type.integer',
  [FieldType.Date]: 'settings.fields.type.date',
  [FieldType.DateTime]: 'settings.fields.type.dateTime',
  [FieldType.Boolean]: 'settings.fields.type.boolean',
  [FieldType.Select]: 'settings.fields.type.select',
  [FieldType.MultiSelect]: 'settings.fields.type.multiSelect',
  [FieldType.Email]: 'settings.fields.type.email',
  [FieldType.Phone]: 'settings.fields.type.phone',
  [FieldType.Url]: 'settings.fields.type.url',
  [FieldType.Currency]: 'settings.fields.type.currency',
  [FieldType.Percent]: 'settings.fields.type.percent',
};

/**
 * Field administration (Definições): the single place to manage the ficha
 * schema — add/remove custom fields and set which are required or reserved,
 * per section. Keeping this out of the collaborator record avoids ad-hoc,
 * unnecessary fields being created during data entry.
 */
export function FieldsManager() {
  const t = useT();
  const [schema, setSchema] = useState<FieldsSchemaDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);
  const [translateMsg, setTranslateMsg] = useState<string | null>(null);

  async function translateMissing() {
    setTranslating(true);
    setTranslateMsg(null);
    setError(null);
    try {
      const res = await api.translateMissingFieldLabels();
      if (!res.configured) {
        setTranslateMsg(t('bilingual.unconfigured'));
      } else if (res.translated === 0) {
        setTranslateMsg(t('settings.fields.translateMissingNone'));
      } else {
        setTranslateMsg(t('settings.fields.translateMissingDone', { n: res.translated }));
        await load();
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setTranslating(false);
    }
  }

  async function load() {
    setError(null);
    try {
      setSchema(await api.fieldsSchema());
    } catch (err) {
      setError((err as Error).message);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  /** Run an action with error handling + reload, flagging the row/section busy. */
  async function run(id: string | null, fn: () => Promise<unknown>) {
    setBusyId(id);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  const sections = (schema?.sections ?? []).filter((s) => s.key !== 'cadastro');

  return (
    <div>
      <p className="muted">
        {t('settings.fields.intro.lead')}
        <strong>{t('settings.fields.intro.tabs')}</strong>
        {t('settings.fields.intro.afterTabs')}
        <strong>{t('settings.fields.intro.required')}</strong>
        {t('settings.fields.intro.afterRequired')}
        <strong>{t('settings.fields.intro.reserved')}</strong>
        {t('settings.fields.intro.afterReserved')}
        <strong>{t('settings.fields.intro.onboarding')}</strong>
        {t('settings.fields.intro.afterOnboarding')}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 12px' }}>
        <button
          type="button"
          className="secondary"
          onClick={() => void translateMissing()}
          disabled={translating}
        >
          {translating ? t('bilingual.translating') : t('settings.fields.translateMissing')}
        </button>
        {translateMsg && <span className="muted">{translateMsg}</span>}
      </div>
      {error && <div className="alert error">{error}</div>}

      {sections.map((section, i) => (
        <SectionBlock
          key={section.key}
          section={section}
          sections={sections}
          isFirst={i === 0}
          isLast={i === sections.length - 1}
          busyId={busyId}
          onPatchField={(id, patch) => void run(id, () => api.updateFieldDefinition(id, patch))}
          onReorderField={(id, dir) => void run(id, () => api.reorderFieldDefinition(id, dir))}
          onToggleSectionReserved={(key, reserved) =>
            void run(null, () => api.updateFieldSection(key, { reserved }))
          }
          onRenameSection={(key, label, labelEn) =>
            void run(null, () => api.updateFieldSection(key, { label, labelEn }))
          }
          onReorderSection={(key, dir) => void run(null, () => api.reorderFieldSection(key, dir))}
          onRemoveField={(id) => {
            if (!window.confirm(t('settings.fields.removeConfirm'))) return;
            void run(id, () => api.deleteFieldDefinition(id));
          }}
          onCreated={() => void load()}
        />
      ))}

      <CreateSectionForm onCreated={() => void load()} />
    </div>
  );
}

function SectionBlock({
  section,
  sections,
  isFirst,
  isLast,
  busyId,
  onPatchField,
  onReorderField,
  onToggleSectionReserved,
  onRenameSection,
  onReorderSection,
  onRemoveField,
  onCreated,
}: {
  section: SchemaSection;
  sections: SchemaSection[];
  isFirst: boolean;
  isLast: boolean;
  busyId: string | null;
  onPatchField: (
    id: string,
    patch: {
      required?: boolean;
      reserved?: boolean;
      onboarding?: boolean;
      sectionKey?: string;
      label?: string;
      labelEn?: string | null;
    },
  ) => void;
  onReorderField: (id: string, dir: 'up' | 'down') => void;
  onToggleSectionReserved: (key: string, reserved: boolean) => void;
  onRenameSection: (key: string, label: string, labelEn: string | null) => void;
  onReorderSection: (key: string, dir: 'up' | 'down') => void;
  onRemoveField: (id: string) => void;
  onCreated: () => void;
}) {
  const t = useT();
  // Sections are collapsible cards; the first opens by default so the screen
  // isn't empty, the rest start closed to keep the overview compact.
  const [collapsed, setCollapsed] = useState(!isFirst);
  const [renaming, setRenaming] = useState(false);
  const [labelPt, setLabelPt] = useState(section.label);
  const [labelEn, setLabelEn] = useState(section.labelEn ?? '');

  // Inline editor for an existing field's label. Built-in fields keep their
  // Portuguese label locked (only the English translation is editable); custom
  // fields allow both. Keys/ids are never touched.
  const [editFieldId, setEditFieldId] = useState<string | null>(null);
  const [fieldPt, setFieldPt] = useState('');
  const [fieldEn, setFieldEn] = useState('');

  function startEditField(def: SchemaSection['fields'][number]) {
    setFieldPt(def.label);
    setFieldEn(def.labelEn ?? '');
    setEditFieldId(def.id);
  }

  function saveEditField(def: SchemaSection['fields'][number], e: React.FormEvent) {
    e.preventDefault();
    const nextPt = fieldPt.trim();
    const nextEn = fieldEn.trim() || null;
    if (def.builtIn) {
      if (nextEn !== (def.labelEn ?? null)) onPatchField(def.id, { labelEn: nextEn });
    } else if (nextPt && (nextPt !== def.label || nextEn !== (def.labelEn ?? null))) {
      onPatchField(def.id, { label: nextPt, labelEn: nextEn });
    }
    setEditFieldId(null);
  }

  function startRename() {
    setLabelPt(section.label);
    setLabelEn(section.labelEn ?? '');
    setRenaming(true);
  }

  function saveRename(e: React.FormEvent) {
    e.preventDefault();
    const nextPt = labelPt.trim();
    if (!nextPt) return;
    const nextEn = labelEn.trim() || null;
    if (nextPt !== section.label || nextEn !== (section.labelEn ?? null)) {
      onRenameSection(section.key, nextPt, nextEn);
    }
    setRenaming(false);
  }

  const count = section.fields.length;

  return (
    <div className={`fcard-section ${collapsed ? 'collapsed' : ''}`}>
      <div
        className="fcard-section-head"
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        title={
          collapsed ? t('settings.fields.expandSection') : t('settings.fields.collapseSection')
        }
        onClick={() => setCollapsed((c) => !c)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCollapsed((c) => !c);
          }
        }}
      >
        <span className="fcard-grip" aria-hidden="true">
          ⠿
        </span>
        <h3 className="fcard-section-title">{section.label}</h3>
        {section.reserved && (
          <span className="fcard-tag reserved">{t('settings.fields.reservedLabel')}</span>
        )}
        <span className="fcard-pill">
          {count === 1
            ? t('settings.fields.fieldCountOne')
            : t('settings.fields.fieldCount', { n: count })}
        </span>
        <span className="fcard-spacer" />
        <span className="fcard-chev" aria-hidden="true">
          {collapsed ? '▸' : '▾'}
        </span>
      </div>

      {!collapsed && (
        <div className="fcard-section-body">
          {renaming ? (
            <form className="fcard-section-tools" onSubmit={saveRename}>
              <BilingualLabelInput
                labelPt={labelPt}
                labelEn={labelEn}
                onChange={(pt, en) => {
                  setLabelPt(pt);
                  setLabelEn(en);
                }}
                ptPlaceholder={t('settings.fields.sectionName')}
                autoFocus
              />
              <div className="actions-row" style={{ gap: 6, marginTop: 8 }}>
                <button type="button" className="secondary" onClick={() => setRenaming(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" disabled={!labelPt.trim()}>
                  {t('common.save')}
                </button>
              </div>
            </form>
          ) : (
            <div className="fcard-section-tools">
              <button
                type="button"
                className="reorder-btn"
                title={t('settings.fields.moveSectionUp')}
                disabled={isFirst}
                onClick={() => onReorderSection(section.key, 'up')}
              >
                ↑
              </button>
              <button
                type="button"
                className="reorder-btn"
                title={t('settings.fields.moveSectionDown')}
                disabled={isLast}
                onClick={() => onReorderSection(section.key, 'down')}
              >
                ↓
              </button>
              <button type="button" className="link-button" onClick={startRename}>
                {t('settings.fields.rename')}
              </button>
              <label className="req-toggle" title={t('settings.fields.sectionReservedHint')}>
                <input
                  type="checkbox"
                  checked={section.reserved}
                  onChange={(e) => onToggleSectionReserved(section.key, e.target.checked)}
                />
                {t('settings.fields.reservedLabel')}
              </label>
            </div>
          )}

          {count === 0 ? (
            <p className="muted" style={{ margin: '4px 2px' }}>
              {t('settings.fields.noFields')}
            </p>
          ) : (
            <div className="fcard-list">
              {section.fields.map((def, i) => {
                const editing = editFieldId === def.id;
                return (
                  <div key={def.id} className={`fcard-field ${editing ? 'editing' : ''}`}>
                    <div className="fcard-field-top">
                      <span className="fcard-grip" aria-hidden="true">
                        ⠿
                      </span>
                      <span className="fcard-field-name">{def.label}</span>
                      {def.builtIn && (
                        <span className="fcard-tag base">{t('settings.fields.baseTag')}</span>
                      )}
                      <span className="fcard-tag type">{t(TYPE_LABEL_KEYS[def.type])}</span>
                      <span className="fcard-spacer" />
                      <button
                        type="button"
                        className="reorder-btn"
                        title={t('settings.common.moveUp')}
                        disabled={i === 0 || busyId === def.id}
                        onClick={() => onReorderField(def.id, 'up')}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="reorder-btn"
                        title={t('settings.common.moveDown')}
                        disabled={i === count - 1 || busyId === def.id}
                        onClick={() => onReorderField(def.id, 'down')}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="fcard-link"
                        disabled={busyId === def.id}
                        onClick={() => (editing ? setEditFieldId(null) : startEditField(def))}
                      >
                        {editing ? t('common.close') : t('common.edit')}
                      </button>
                    </div>

                    {editing ? (
                      <form className="fcard-edit" onSubmit={(e) => saveEditField(def, e)}>
                        <BilingualLabelInput
                          labelPt={fieldPt}
                          labelEn={fieldEn}
                          onChange={(pt, en) => {
                            setFieldPt(pt);
                            setFieldEn(en);
                          }}
                          ptLocked={def.builtIn}
                          autoFocus
                        />
                        <div className="fcard-edit-row">
                          <label className="fcard-move">
                            {t('settings.fields.col.section')}
                            <select
                              value={section.key}
                              disabled={busyId === def.id}
                              onChange={(e) => onPatchField(def.id, { sectionKey: e.target.value })}
                            >
                              {sections.map((s) => (
                                <option key={s.key} value={s.key}>
                                  {s.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <span className="fcard-spacer" />
                          {!def.builtIn && (
                            <button
                              type="button"
                              className="link-button danger"
                              disabled={busyId === def.id}
                              onClick={() => onRemoveField(def.id)}
                            >
                              {t('common.remove')}
                            </button>
                          )}
                          <button type="submit">{t('common.save')}</button>
                        </div>
                      </form>
                    ) : (
                      <div className="fcard-field-en">
                        <span className="bilingual-tag">EN</span>
                        {def.labelEn?.trim() ? def.labelEn : '—'}
                      </div>
                    )}

                    <div className="fcard-flags">
                      <button
                        type="button"
                        className={`fcard-flag ${def.required ? 'on' : ''}`}
                        disabled={busyId === def.id}
                        onClick={() => onPatchField(def.id, { required: !def.required })}
                      >
                        {def.required ? '✓ ' : ''}
                        {t('settings.fields.flag.required')}
                      </button>
                      <button
                        type="button"
                        className={`fcard-flag ${def.reserved ? 'on' : ''}`}
                        disabled={busyId === def.id}
                        onClick={() => onPatchField(def.id, { reserved: !def.reserved })}
                      >
                        {def.reserved ? '✓ ' : ''}
                        {t('settings.fields.flag.reserved')}
                      </button>
                      <button
                        type="button"
                        className={`fcard-flag ${def.onboarding ? 'on' : ''}`}
                        disabled={busyId === def.id}
                        title={t('settings.fields.col.onboardingHint')}
                        onClick={() => onPatchField(def.id, { onboarding: !def.onboarding })}
                      >
                        {def.onboarding ? '✓ ' : ''}
                        {t('settings.fields.flag.onboarding')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <AddFieldForm sectionKey={section.key} onCreated={onCreated} />
        </div>
      )}
    </div>
  );
}

function CreateSectionForm({ onCreated }: { onCreated: () => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [labelPt, setLabelPt] = useState('');
  const [labelEn, setLabelEn] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!labelPt.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.createFieldSection({ label: labelPt.trim(), labelEn: labelEn.trim() || null });
      setLabelPt('');
      setLabelEn('');
      setOpen(false);
      onCreated();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="secondary add-field" onClick={() => setOpen(true)}>
        {t('settings.fields.newSection')}
      </button>
    );
  }
  return (
    <form className="add-field-form" onSubmit={submit}>
      <h3>{t('settings.fields.newSectionTitle')}</h3>
      {error && <div className="alert error">{error}</div>}
      <label>
        {t('settings.fields.sectionName')}
        <BilingualLabelInput
          labelPt={labelPt}
          labelEn={labelEn}
          onChange={(pt, en) => {
            setLabelPt(pt);
            setLabelEn(en);
          }}
          autoFocus
        />
      </label>
      <div className="actions-row">
        <button type="button" className="secondary" onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </button>
        <button type="submit" disabled={busy}>
          {busy ? t('settings.fields.creating') : t('settings.fields.createSection')}
        </button>
      </div>
    </form>
  );
}

function AddFieldForm({ sectionKey, onCreated }: { sectionKey: string; onCreated: () => void }) {
  const tr = useT();
  const [open, setOpen] = useState(false);
  const [labelPt, setLabelPt] = useState('');
  const [labelEn, setLabelEn] = useState('');
  const [type, setType] = useState<FieldType>(FieldType.Text);
  const [options, setOptions] = useState<FieldOption[]>([]);
  const [reserved, setReserved] = useState(false);
  const [required, setRequired] = useState(false);
  const [onboarding, setOnboarding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsOptions = OPTION_FIELD_TYPES.includes(type);

  // Seed a single blank option row when switching to a type that needs options,
  // so the editor is never shown empty with no way in.
  useEffect(() => {
    if (needsOptions && options.length === 0) {
      setOptions([{ value: '', label: '', labelEn: null }]);
    }
  }, [needsOptions, options.length]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const cleanOptions = needsOptions
        ? options
            .map((o) => ({
              value: o.label.trim(),
              label: o.label.trim(),
              labelEn: (o.labelEn ?? '').trim() || null,
            }))
            .filter((o) => o.label)
        : undefined;
      await api.createFieldDefinition({
        sectionKey,
        label: labelPt.trim(),
        labelEn: labelEn.trim() || null,
        type,
        options: cleanOptions,
        reserved,
        required,
        onboarding,
      });
      setLabelPt('');
      setLabelEn('');
      setOptions([]);
      setReserved(false);
      setRequired(false);
      setOnboarding(false);
      setOpen(false);
      onCreated();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="secondary add-field" onClick={() => setOpen(true)}>
        {tr('settings.fields.newField')}
      </button>
    );
  }

  return (
    <form className="add-field-form" onSubmit={submit}>
      <h3>{tr('settings.fields.newFieldIn', { section: sectionKey })}</h3>
      {error && <div className="alert error">{error}</div>}
      <div className="row">
        <label>
          {tr('settings.fields.fieldName')}
          <BilingualLabelInput
            labelPt={labelPt}
            labelEn={labelEn}
            onChange={(pt, en) => {
              setLabelPt(pt);
              setLabelEn(en);
            }}
            autoFocus
          />
        </label>
        <label>
          {tr('settings.fields.typeLabel')}
          <select value={type} onChange={(e) => setType(e.target.value as FieldType)}>
            {Object.values(FieldType).map((t) => (
              <option key={t} value={t}>
                {tr(TYPE_LABEL_KEYS[t])}
              </option>
            ))}
          </select>
        </label>
      </div>
      {needsOptions && <OptionsEditor options={options} onChange={setOptions} />}
      <label className="section-reserved">
        <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
        {tr('settings.fields.requiredField')}
      </label>
      <label className="section-reserved">
        <input type="checkbox" checked={reserved} onChange={(e) => setReserved(e.target.checked)} />
        {tr('settings.fields.reservedField')}
      </label>
      <label className="section-reserved">
        <input
          type="checkbox"
          checked={onboarding}
          onChange={(e) => setOnboarding(e.target.checked)}
        />
        {tr('settings.fields.onboardingField')}
      </label>
      <div className="actions-row">
        <button type="button" className="secondary" onClick={() => setOpen(false)}>
          {tr('common.cancel')}
        </button>
        <button type="submit" disabled={busy}>
          {busy ? tr('settings.fields.creating') : tr('settings.fields.createField')}
        </button>
      </div>
    </form>
  );
}

/**
 * Per-option bilingual editor for SELECT / MULTISELECT fields. Each row holds a
 * Portuguese label (the source, also used as the option's stored value) plus an
 * optional English label. Reuses {@link BilingualLabelInput} so an option can be
 * machine-translated like any other label.
 */
function OptionsEditor({
  options,
  onChange,
  disabled,
}: {
  options: FieldOption[];
  onChange: (next: FieldOption[]) => void;
  disabled?: boolean;
}) {
  const t = useT();

  function update(index: number, pt: string, en: string) {
    onChange(options.map((o, i) => (i === index ? { value: o.value, label: pt, labelEn: en } : o)));
  }

  function remove(index: number) {
    onChange(options.filter((_, i) => i !== index));
  }

  function add() {
    onChange([...options, { value: '', label: '', labelEn: null }]);
  }

  return (
    <div className="options-editor">
      <span className="options-editor-caption">{t('settings.fields.options')}</span>
      {options.map((option, i) => (
        <div
          key={i}
          className="option-row"
          style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8 }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <BilingualLabelInput
              labelPt={option.label}
              labelEn={option.labelEn}
              onChange={(pt, en) => update(i, pt, en)}
              disabled={disabled}
            />
          </div>
          <button
            type="button"
            className="link-button"
            onClick={() => remove(i)}
            disabled={disabled}
          >
            {t('common.remove')}
          </button>
        </div>
      ))}
      <div className="actions-row" style={{ marginTop: 8 }}>
        <button type="button" className="secondary" onClick={add} disabled={disabled}>
          {t('common.add')}
        </button>
      </div>
    </div>
  );
}
